import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, students, users } from "./schemaBase.js";

/**
 * Adult contact workflow only. A prospect is not a learner and remains outside
 * the canonical student/account tables until a staff member records verified
 * family-account evidence.
 */
export const enquiryPipelineItems = pgTable(
  "enquiry_pipeline_items",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    source: text("source").notNull().default("manual"),
    instrumentInterest: varchar("instrument_interest", { length: 120 }),
    preferredAvailability: varchar("preferred_availability", { length: 300 }),
    publicMessage: varchar("public_message", { length: 1000 }),
    sourceSubmittedAt: timestamp("source_submitted_at", { withTimezone: true }),
    intakeDigest: varchar("intake_digest", { length: 64 }),
    contactName: text("contact_name").notNull(),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    workflowStatus: text("workflow_status").notNull().default("enquiry"),
    assignedToUserId: varchar("assigned_to_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    verifiedStudentId: varchar("verified_student_id").references(() => students.id, {
      onDelete: "set null",
    }),
    verifiedGuardianUserId: varchar("verified_guardian_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    identityVerifiedAt: timestamp("identity_verified_at", { withTimezone: true }),
    identityVerifiedByUserId: varchar("identity_verified_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    verificationMethod: text("verification_method"),
    verificationReference: varchar("verification_reference", { length: 120 }),
    nextActionAt: timestamp("next_action_at", { withTimezone: true }),
    staffNote: varchar("staff_note", { length: 500 }),
    revision: integer("revision").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("enquiry_pipeline_items_org_idempotency_idx").on(
      t.organisationId,
      t.idempotencyKey,
    ),
    index("enquiry_pipeline_items_org_status_updated_idx").on(
      t.organisationId,
      t.workflowStatus,
      t.updatedAt,
    ),
    index("enquiry_pipeline_items_org_assignee_status_idx").on(
      t.organisationId,
      t.assignedToUserId,
      t.workflowStatus,
    ),
    check(
      "enquiry_pipeline_items_status_check",
      sql.raw("workflow_status in ('enquiry', 'awaiting_availability', 'taster_offered', 'follow_up', 'closed')"),
    ),
    check(
      "enquiry_pipeline_items_contact_check",
      sql.raw("contact_email is not null or contact_phone is not null"),
    ),
    check("enquiry_pipeline_items_source_check", sql.raw("source in ('manual', 'website')")),
    check("enquiry_pipeline_items_revision_check", sql.raw("revision > 0")),
    check(
      "enquiry_pipeline_items_verification_method_check",
      sql.raw(
        "verification_method is null or verification_method in ('confirmed_hub_conversation', 'direct_account_confirmation', 'in_person_confirmation', 'verified_booking_reference')",
      ),
    ),
  ],
);

export const enquiryPipelineEvents = pgTable(
  "enquiry_pipeline_events",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    itemId: varchar("item_id")
      .notNull()
      .references(() => enquiryPipelineItems.id, { onDelete: "cascade" }),
    eventType: text("event_type").notNull(),
    fromStatus: text("from_status"),
    toStatus: text("to_status"),
    actorUserId: varchar("actor_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    verificationMethod: text("verification_method"),
    evidenceReference: varchar("evidence_reference", { length: 120 }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("enquiry_pipeline_events_item_created_idx").on(
      t.organisationId,
      t.itemId,
      t.createdAt.desc(),
    ),
    check(
      "enquiry_pipeline_events_type_check",
      sql.raw("event_type in ('created', 'status_changed', 'note_updated', 'identity_verified', 'identity_cleared', 'follow_up_recorded')"),
    ),
    check(
      "enquiry_pipeline_events_verification_method_check",
      sql.raw(
        "verification_method is null or verification_method in ('confirmed_hub_conversation', 'direct_account_confirmation', 'in_person_confirmation', 'verified_booking_reference')",
      ),
    ),
  ],
);

